import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  CheckCircle2,
  ChevronDown,
  Edit3,
  Eye,
  FileText,
  MapPin,
  Percent,
  Plus,
  RefreshCw,
  Save,
  Store,
  UploadCloud,
  XCircle,
} from "lucide-react";

import { restaurantAdminService, rowsOf } from "../../services/restaurantAdmin.service";
import { env } from "../../config/env";
import {
  isBase64Image,
  resolveRestaurantFoodImageUrl,
} from "../../utils/restaurantFoodImageUrl";
import {
  Button,
  Card,
  EmptyState,
  ErrorBox,
  Input,
  LoadingBox,
  PageHeader,
  Select,
  StatusPill,
  Switch,
  TableShell,
  Textarea,
  restaurantIdOf,
} from "./components/FoodAdminUi";

const MAX_RESTAURANT_IMAGE_SIZE = 3 * 1024 * 1024;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

function resolveAuthUploadUrl(value) {
  const url = typeof value === "string" ? value : value?.url || value?.path || "";
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;

  return `${String(env.AUTH_BASE_URL || "").replace(/\/$/, "")}/${String(url).replace(/^\//, "")}`;
}

function normalizeDocValue(value) {
  if (!value) return { url: "" };
  if (typeof value === "string") return { url: value };
  return { ...value, url: value.url || value.path || "" };
}

function registrationPayloadOf(req = {}) {
  return req.payload || req.requestPayload || req.request_payload || {};
}

function registrationFormFromRequest(req = {}) {
  const payload = registrationPayloadOf(req);
  const address = payload.address || {};
  const bank = payload.bank || {};
  const docs = req.documents || payload.documents || {};

  return {
    id: req.id || req.requestId,
    restaurantName:
      req.restaurantName ||
      req.restaurant_name ||
      payload.restaurantName ||
      "",
    restaurantCode:
      req.restaurantCode ||
      req.restaurant_code ||
      payload.restaurantCode ||
      "",
    description: payload.description || "",
    fssaiNumber: payload.fssaiNumber || payload.fssai_number || "",
    gstNumber: payload.gstNumber || payload.gst_number || "",
    panNumber: payload.panNumber || payload.pan_number || "",
    ownerName: req.ownerName || req.owner_name || payload.ownerName || "",
    ownerEmail: req.ownerEmail || req.owner_email || payload.ownerEmail || "",
    ownerPhone: req.ownerPhone || req.owner_phone || payload.ownerPhone || "",
    fatherName:
      req.fatherName ||
      req.father_name ||
      req.ownerFatherName ||
      req.owner_father_name ||
      payload.fatherName ||
      payload.father_name ||
      payload.ownerFatherName ||
      payload.owner_father_name ||
      "",
    dateOfBirth:
      req.dateOfBirth ||
      req.date_of_birth ||
      req.dob ||
      payload.dateOfBirth ||
      payload.date_of_birth ||
      payload.dob ||
      "",
    gender: req.gender || payload.gender || "",
    aadhaarNumber:
      req.aadhaarNumber ||
      req.aadhaar_number ||
      req.aadharNumber ||
      req.aadhar_number ||
      payload.aadhaarNumber ||
      payload.aadhaar_number ||
      payload.aadharNumber ||
      payload.aadhar_number ||
      "",
    address: {
      addressLine1:
        address.addressLine1 ||
        address.address_line1 ||
        payload.addressLine1 ||
        "",
      addressLine2:
        address.addressLine2 ||
        address.address_line2 ||
        payload.addressLine2 ||
        "",
      city: address.city || payload.city || "",
      state: address.state || payload.state || "",
      pincode: address.pincode || payload.pincode || "",
      lat: address.lat ?? payload.lat ?? "",
      lng: address.lng ?? payload.lng ?? "",
    },
    bank: {
      accountHolderName:
        bank.accountHolderName ||
        bank.account_holder_name ||
        payload.accountHolderName ||
        "",
      bankName: bank.bankName || bank.bank_name || payload.bankName || "",
      accountNumber:
        bank.accountNumber ||
        bank.account_number ||
        payload.accountNumber ||
        "",
      ifscCode: bank.ifscCode || bank.ifsc_code || payload.ifscCode || "",
      accountType:
        bank.accountType ||
        bank.account_type ||
        payload.accountType ||
        payload.account_type ||
        "",
      upiId: bank.upiId || bank.upi_id || payload.upiId || "",
    },
    serviceRadiusKm: payload.serviceRadiusKm ?? payload.service_radius_km ?? 5,
    minOrderValue: payload.minOrderValue ?? payload.min_order_value ?? 0,
    packagingCharge: payload.packagingCharge ?? payload.packaging_charge ?? 0,
    preparationTimeMins:
      payload.preparationTimeMins ?? payload.preparation_time_mins ?? 20,
    deliveryTimeMin: payload.deliveryTimeMin ?? payload.delivery_time_min ?? 30,
    deliveryTimeMax: payload.deliveryTimeMax ?? payload.delivery_time_max ?? 45,
    adminCommissionPercent:
      req.adminCommissionPercent ??
      req.admin_commission_percent ??
      payload.adminCommissionPercent ??
      0,
    documents: {
      ...Object.fromEntries(
        Object.entries(docs || {}).map(([key, value]) => [
          key,
          normalizeDocValue(value),
        ])
      ),
      aadhaar: normalizeDocValue(
        docs.aadhaar ||
        docs.aadhar ||
        docs.aadhaarImage ||
        docs.aadhaar_image ||
        payload.aadhaarImage ||
        payload.aadhaar_image ||
        payload.aadhaarImageUrl ||
        ""
      ),
    },
    acceptedTerms: payload.acceptedTerms ?? payload.accepted_terms ?? true,
  };
}

function makeRegistrationUpdatePayload(form = {}) {
  return {
    restaurantName: form.restaurantName,
    restaurantCode: form.restaurantCode,
    description: form.description,
    fssaiNumber: form.fssaiNumber,
    gstNumber: form.gstNumber,
    panNumber: form.panNumber,
    ownerName: form.ownerName,
    ownerEmail: form.ownerEmail,
    ownerPhone: form.ownerPhone,
    fatherName: form.fatherName,
    dateOfBirth: form.dateOfBirth,
    gender: form.gender,
    aadhaarNumber: form.aadhaarNumber,
    address: form.address || {},
    bank: form.bank || {},
    serviceRadiusKm: Number(form.serviceRadiusKm || 0),
    minOrderValue: Number(form.minOrderValue || 0),
    packagingCharge: Number(form.packagingCharge || 0),
    preparationTimeMins: Number(form.preparationTimeMins || 0),
    deliveryTimeMin: Number(form.deliveryTimeMin || 0),
    deliveryTimeMax: Number(form.deliveryTimeMax || 0),
    adminCommissionPercent: Number(form.adminCommissionPercent || 0),
    documents: form.documents || {},
    acceptedTerms: form.acceptedTerms !== false,
  };
}

function unwrapRegistrationRequest(res) {
  return res?.request || res?.data?.request || res?.data || res;
}

function ImageUploadField({ label, value, onUploaded }) {
  const inputId = React.useId();

  async function onFile(e) {
    const file = e.target.files?.[0];

    try {
      if (!file) return;

      if (!String(file.type || "").startsWith("image/")) {
        throw new Error("Only image files are allowed");
      }

      if (file.size > MAX_RESTAURANT_IMAGE_SIZE) {
        throw new Error("Image must be 3 MB or less");
      }

      const res = await restaurantAdminService.uploadFoodImage(file);
      const url =
        res?.data?.imageUrl ||
        res?.data?.url ||
        res?.imageUrl ||
        res?.url;

      if (!url) throw new Error("Upload failed: image URL missing");

      onUploaded(url);
    } catch (err) {
      alert(err?.message || "Image upload failed");
    } finally {
      e.target.value = "";
    }
  }

  const preview =
    value && !isBase64Image(value) ? resolveRestaurantFoodImageUrl(value) : "";

  return (
    <div>
      <div className="mb-1 text-[12px] font-medium text-slate-500">
        {label}
      </div>

      <div className="flex min-h-[44px] items-center gap-3 rounded-[8px] border border-[#f2c6cc] bg-white px-3 py-2">
        {preview ? (
          <img
            src={preview}
            alt={label}
            className="h-9 w-9 rounded-lg object-cover"
          />
        ) : (
          <div className="h-9 w-9 rounded-lg bg-[#fff0f2]" />
        )}

        <div className="min-w-0 flex-1 truncate text-[11px] font-medium text-slate-500">
          {preview || "No image uploaded"}
        </div>

        <label
          htmlFor={inputId}
          className="inline-flex cursor-pointer items-center gap-1 rounded-lg bg-[#fff0f2] px-3 py-2 text-[11px] font-semibold text-[#d86a78]"
        >
          <UploadCloud size={13} />
          Upload
        </label>

        <input
          id={inputId}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onFile}
        />
      </div>
    </div>
  );
}

function ActionIconButton({ title, onClick, children, tone = "default" }) {
  const toneClass =
    tone === "pink"
      ? "border-[#f2c6cc] text-[#d86a78] hover:bg-[#fff1f3]"
      : tone === "green"
        ? "border-emerald-100 text-emerald-600 hover:bg-emerald-50"
        : tone === "red"
          ? "border-rose-100 text-rose-500 hover:bg-rose-50"
          : "border-slate-100 text-slate-500 hover:bg-slate-50";

  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      className={`inline-flex h-9 w-9 items-center justify-center rounded-[10px] border bg-white transition ${toneClass}`}
    >
      {children}
    </button>
  );
}

function PaginationBar({
  page,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  itemLabel = "items",
}) {
  const safeTotalPages = Math.max(1, totalPages || 1);
  const safePage = Math.min(Math.max(1, page || 1), safeTotalPages);

  if (!totalItems) return null;

  return (
    <div className="mt-4 flex flex-col gap-3 rounded-[20px] border border-[#f2c6cc] bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="text-[12px] font-medium text-slate-500">
        Total {totalItems} {itemLabel}
      </div>

      <div className="flex flex-wrap items-center justify-start gap-3 sm:justify-end">
        <label className="flex items-center gap-2 text-[12px] font-medium text-slate-500">
          Rows
          <select
            value={pageSize}
            onChange={(e) => {
              onPageSizeChange(Number(e.target.value));
              onPageChange(1);
            }}
            className="h-10 rounded-[12px] border border-[#f2c6cc] bg-white px-3 text-[13px] font-semibold text-slate-700 outline-none transition focus:border-[#d86a78] focus:ring-4 focus:ring-[#d86a78]/10"
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          onClick={() => onPageChange(Math.max(1, safePage - 1))}
          disabled={safePage <= 1}
          className="h-10 rounded-[12px] border border-[#f2c6cc] bg-white px-4 text-[12px] font-semibold text-[#d86a78] transition hover:bg-[#fff6f7] disabled:cursor-not-allowed disabled:opacity-50"
        >
          Prev
        </button>

        <div className="h-10 rounded-[12px] bg-white px-3 py-2 text-[12px] font-semibold text-slate-600">
          Page {safePage} / {safeTotalPages}
        </div>

        <button
          type="button"
          onClick={() => onPageChange(Math.min(safeTotalPages, safePage + 1))}
          disabled={safePage >= safeTotalPages}
          className="h-10 rounded-[12px] border border-[#f2c6cc] bg-white px-4 text-[12px] font-semibold text-[#d86a78] transition hover:bg-[#fff6f7] disabled:cursor-not-allowed disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
}

const STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "APPROVED", label: "Approved" },
  { value: "PENDING", label: "Pending" },
  { value: "SUSPENDED", label: "Suspended" },
  { value: "BLOCKED", label: "Blocked" },
];

function StatusFilter({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const selected =
    STATUS_OPTIONS.find((option) => option.value === value) || STATUS_OPTIONS[0];

  useEffect(() => {
    function handleClickOutside(event) {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(event.target)) setOpen(false);
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function selectOption(nextValue) {
    onChange(nextValue);
    setOpen(false);
  }

  return (
    <div ref={rootRef} className="relative">
      <div className="mb-1 text-[12px] font-medium text-slate-500">Status</div>

      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className={`flex h-11 w-full items-center justify-between gap-3 rounded-[13px] border bg-white px-3 text-left text-sm font-medium outline-none transition ${open
          ? "border-[#d86a78] ring-4 ring-rose-100"
          : "border-[#f2c6cc] hover:border-[#d86a78]"
          }`}
      >
        <span className={value ? "text-slate-800" : "text-slate-500"}>
          {selected.label}
        </span>
        <ChevronDown
          size={16}
          className={`text-[#d86a78] transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open ? (
        <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-50 overflow-hidden rounded-[14px] border border-[#f2c6cc] bg-white p-1 shadow-[0_18px_45px_rgba(216,106,120,0.20)]">
          {STATUS_OPTIONS.map((option) => {
            const active = option.value === value;

            return (
              <button
                key={option.value || "ALL"}
                type="button"
                onClick={() => selectOption(option.value)}
                className={`flex w-full items-center justify-between rounded-[10px] px-3 py-2.5 text-left text-[13px] font-semibold transition ${active
                  ? "bg-[#fff1f3] text-[#d86a78]"
                  : "text-slate-600 hover:bg-[#fff8f9] hover:text-[#d86a78]"
                  }`}
              >
                <span>{option.label}</span>
                {active ? <span className="h-2 w-2 rounded-full bg-[#d86a78]" /> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function SummaryMetricCard({ label, value, tone = "pink" }) {
  const toneClasses = {
    pink: "border-[#f2c6cc] bg-white text-[#d86a78]",
    yellow: "border-[#f2c6cc] bg-white text-[#d86a78]",
    green: "border-[#f2c6cc] bg-white text-[#d86a78]",
    red: "border-[#f2c6cc] bg-white text-[#d86a78]",
  };

  return (
    <div className={`rounded-[18px] border p-4 sm:p-5 ${toneClasses[tone] || toneClasses.pink}`}>
      <div className="text-[11px] font-bold uppercase tracking-wide opacity-90">
        {label}
      </div>
      <div className="mt-2 text-[26px] font-bold leading-none sm:text-[28px]">
        {value}
      </div>
    </div>
  );
}

const blankRestaurant = {
  code: "",
  name: "",
  phone: "",
  email: "",
  fatherName: "",
  dateOfBirth: "",
  gender: "",
  aadhaarNumber: "",
  accountType: "SAVINGS",
  aadhaarImageUrl: "",
  description: "",
  logoUrl: "",
  coverImageUrl: "",
  serviceRadiusKm: 5,
  minOrderValue: 99,
  packagingCharge: 0,
  adminCommissionPercent: 0,
  preparationTimeMins: 20,
  deliveryTimeMin: 30,
  deliveryTimeMax: 50,
  ratingAvg: 4.2,
  ratingCount: 0,
  isActive: true,
  isOpen: true,
  isPermanentlyClosed: false,
  permanentlyClosedAt: "",
  permanentlyClosedBy: "",
  permanentCloseReason: "",
  isFeatured: false,
  isRecommended: false,
  status: "APPROVED",
  address: {
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    pincode: "",
    lat: "",
    lng: "",
  },
  bank: {
    accountType: "SAVINGS",
  },
  documents: {
    aadhaar: { url: "" },
  },
};

function boolOf(value, fallback = false) {
  if (value === undefined || value === null || value === "") return fallback;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  const clean = String(value).trim().toLowerCase();
  if (["true", "1", "yes", "y"].includes(clean)) return true;
  if (["false", "0", "no", "n"].includes(clean)) return false;
  return fallback;
}

function isRestaurantPermanentlyClosed(row = {}) {
  return boolOf(row?.isPermanentlyClosed ?? row?.is_permanently_closed, false);
}

function permanentCloseReasonOf(row = {}) {
  return row?.permanentCloseReason || row?.permanent_close_reason || "";
}

function restaurantDisplayName(row = {}) {
  return row?.name || row?.restaurantName || row?.restaurant_name || "Restaurant";
}

function normalizeRestaurant(row) {
  const address = row?.address || {};

  return {
    ...blankRestaurant,
    ...row,
    code: row?.code || "",
    name: row?.name || "",
    phone: row?.phone || "",
    email: row?.email || "",
    fatherName: row?.fatherName || row?.father_name || "",
    dateOfBirth: row?.dateOfBirth || row?.date_of_birth || row?.dob || "",
    gender: row?.gender || "",
    aadhaarNumber:
      row?.aadhaarNumber ||
      row?.aadhaar_number ||
      row?.aadharNumber ||
      row?.aadhar_number ||
      "",
    accountType:
      row?.accountType ||
      row?.account_type ||
      row?.bank?.accountType ||
      row?.bank?.account_type ||
      "SAVINGS",
    aadhaarImageUrl:
      row?.aadhaarImageUrl ||
      row?.aadhaar_image_url ||
      row?.documents?.aadhaar?.url ||
      row?.documents?.aadhaar ||
      "",
    description: row?.description || "",
    logoUrl: row?.logoUrl || row?.logo_url || "",
    coverImageUrl: row?.coverImageUrl || row?.cover_image_url || "",
    serviceRadiusKm: row?.serviceRadiusKm ?? row?.service_radius_km ?? 5,
    minOrderValue: row?.minOrderValue ?? row?.min_order_value ?? 99,
    packagingCharge: row?.packagingCharge ?? row?.packaging_charge ?? 0,
    adminCommissionPercent:
      row?.adminCommissionPercent ??
      row?.admin_commission_percent ??
      row?.commissionPercent ??
      0,
    preparationTimeMins:
      row?.preparationTimeMins ?? row?.preparation_time_mins ?? 20,
    deliveryTimeMin: row?.deliveryTimeMin ?? row?.delivery_time_min ?? 30,
    deliveryTimeMax: row?.deliveryTimeMax ?? row?.delivery_time_max ?? 50,
    ratingAvg: row?.ratingAvg ?? row?.rating_avg ?? 4.2,
    ratingCount: row?.ratingCount ?? row?.rating_count ?? 0,
    isActive: boolOf(row?.isActive ?? row?.is_active, true),
    isOpen: boolOf(row?.isOpen ?? row?.is_open, true),
    isPermanentlyClosed: isRestaurantPermanentlyClosed(row),
    permanentlyClosedAt: row?.permanentlyClosedAt || row?.permanently_closed_at || "",
    permanentlyClosedBy: row?.permanentlyClosedBy || row?.permanently_closed_by || "",
    permanentCloseReason: permanentCloseReasonOf(row),
    isFeatured: boolOf(row?.isFeatured ?? row?.is_featured, false),
    isRecommended: boolOf(row?.isRecommended ?? row?.is_recommended, false),
    status: row?.status || "APPROVED",
    bank: {
      ...(row?.bank || {}),
      accountType:
        row?.accountType ||
        row?.account_type ||
        row?.bank?.accountType ||
        row?.bank?.account_type ||
        "SAVINGS",
    },
    documents: {
      ...(row?.documents || {}),
      aadhaar: normalizeDocValue(
        row?.documents?.aadhaar ||
        row?.documents?.aadhar ||
        row?.aadhaarImageUrl ||
        row?.aadhaar_image_url ||
        ""
      ),
    },
    address: {
      addressLine1:
        address?.addressLine1 ||
        address?.address_line1 ||
        row?.addressLine1 ||
        row?.address_line1 ||
        "",
      addressLine2:
        address?.addressLine2 ||
        address?.address_line2 ||
        row?.addressLine2 ||
        row?.address_line2 ||
        "",
      city: address?.city || row?.city || "",
      state: address?.state || row?.state || "",
      pincode: address?.pincode || row?.pincode || "",
      lat: address?.lat ?? address?.latitude ?? row?.lat ?? row?.latitude ?? "",
      lng:
        address?.lng ??
        address?.longitude ??
        row?.lng ??
        row?.longitude ??
        "",
    },
  };
}

function makePayload(form) {
  const {
    isPermanentlyClosed,
    is_permanently_closed,
    permanentlyClosedAt,
    permanently_closed_at,
    permanentlyClosedBy,
    permanently_closed_by,
    permanentCloseReason,
    permanent_close_reason,
    ...safeForm
  } = form || {};

  return {
    ...safeForm,
    serviceRadiusKm: Number(form.serviceRadiusKm || 0),
    minOrderValue: Number(form.minOrderValue || 0),
    packagingCharge: Number(form.packagingCharge || 0),
    adminCommissionPercent: Number(form.adminCommissionPercent || 0),
    preparationTimeMins: Number(form.preparationTimeMins || 0),
    deliveryTimeMin: Number(form.deliveryTimeMin || 0),
    deliveryTimeMax: Number(form.deliveryTimeMax || 0),
    ratingAvg: Number(form.ratingAvg || 0),
    ratingCount: Number(form.ratingCount || 0),
    fatherName: form.fatherName || "",
    dateOfBirth: form.dateOfBirth || "",
    gender: form.gender || "",
    aadhaarNumber: form.aadhaarNumber || "",
    accountType: form.accountType || form.bank?.accountType || "",
    bank: {
      ...(form.bank || {}),
      accountType: form.accountType || form.bank?.accountType || "",
    },
    documents: {
      ...(form.documents || {}),
      aadhaar: {
        ...(form.documents?.aadhaar || {}),
        url: form.aadhaarImageUrl || form.documents?.aadhaar?.url || "",
      },
    },
    address: {
      ...form.address,
      lat: Number(form.address?.lat || 0),
      lng: Number(form.address?.lng || 0),
    },
  };
}

export default function FoodRestaurantsPage() {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [registrationRequests, setRegistrationRequests] = useState([]);
  const [requestCuts, setRequestCuts] = useState({});
  const [reviewingId, setReviewingId] = useState("");
  const [reviewRequest, setReviewRequest] = useState(null);
  const [reviewForm, setReviewForm] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewSaving, setReviewSaving] = useState(false);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [view, setView] = useState("list");
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(blankRestaurant);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [popupMessage, setPopupMessage] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [requestPage, setRequestPage] = useState(1);
  const [requestPageSize, setRequestPageSize] = useState(10);

  const stats = useMemo(() => {
    const total = rows.length;
    const active = rows.filter((r) => boolOf(r.isActive ?? r.is_active, false)).length;
    const open = rows.filter((r) => boolOf(r.isOpen ?? r.is_open, false)).length;
    const permanentClosed = rows.filter(isRestaurantPermanentlyClosed).length;

    return {
      total,
      active,
      open,
      permanentClosed,
      pendingRequests: registrationRequests.length,
    };
  }, [rows, registrationRequests]);

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function setAddress(key, value) {
    setForm((prev) => ({
      ...prev,
      address: {
        ...(prev.address || {}),
        [key]: value,
      },
    }));
  }

  async function load() {
    setLoading(true);
    setError("");

    try {
      const [res, pendingRes] = await Promise.all([
        restaurantAdminService.listRestaurants({ q, status, limit: 300 }),
        restaurantAdminService.listRestaurantRegistrationRequests({
          status: "PENDING",
          limit: 50,
        }),
      ]);

      setRows(rowsOf(res));

      const pendingRows = rowsOf(pendingRes);
      setRegistrationRequests(pendingRows);

      setRequestCuts((prev) => {
        const next = { ...prev };

        pendingRows.forEach((req) => {
          const id = req.id || req.requestId;

          if (id && next[id] == null) {
            next[id] =
              req.adminCommissionPercent ??
              req.admin_commission_percent ??
              0;
          }
        });

        return next;
      });
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setPage(1);
      setRequestPage(1);
      load();
    }, 350);

    return () => window.clearTimeout(timer);
  }, [q, status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const nextTotalPages = Math.max(1, Math.ceil(rows.length / pageSize));
    if (page > nextTotalPages) {
      setPage(nextTotalPages);
    }
  }, [rows.length, page, pageSize]);

  useEffect(() => {
    const nextTotalPages = Math.max(
      1,
      Math.ceil(registrationRequests.length / requestPageSize)
    );

    if (requestPage > nextTotalPages) {
      setRequestPage(nextTotalPages);
    }
  }, [registrationRequests.length, requestPage, requestPageSize]);

  function startCreate() {
    setSelected(null);
    setForm(blankRestaurant);
    setView("form");
  }

  function startEdit(row) {
    const id = restaurantIdOf(row);
    if (!id) return;
    navigate(`/admin/food-dashboard/restaurants/${id}?edit=1`);
  }

  async function submit() {
    if (!form.name) {
      setError("Restaurant name is required");
      return;
    }

    setSaving(true);
    setError("");

    try {
      const payload = makePayload(form);

      if (selected) {
        await restaurantAdminService.updateRestaurant(
          restaurantIdOf(selected),
          payload
        );
      } else {
        await restaurantAdminService.createRestaurant(payload);
      }

      setView("list");
      setSelected(null);
      setForm(blankRestaurant);

      await load();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }

  async function toggle(row, key) {
    setError("");

    if (isRestaurantPermanentlyClosed(row)) {
      setPopupMessage("This restaurant is permanently closed. Reopen it before changing Open/Active status.");
      return;
    }

    try {
      const isOpen = boolOf(row.isOpen ?? row.is_open, false);
      const isActive = boolOf(row.isActive ?? row.is_active, false);

      const payload =
        key === "open"
          ? { isOpen: !isOpen, isActive }
          : { isActive: !isActive, isOpen };

      await restaurantAdminService.updateRestaurant(restaurantIdOf(row), payload);
      await load();
    } catch (err) {
      setError(err);
    }
  }

  async function togglePermanentClose(row) {
    const restaurantId = restaurantIdOf(row);
    if (!restaurantId) return;

    const closed = isRestaurantPermanentlyClosed(row);
    const name = restaurantDisplayName(row);

    if (closed) {
      const confirmed = window.confirm(
        `Reopen ${name}? Restaurant employees will be able to login again after this.`
      );
      if (!confirmed) return;
    } else {
      const reason = window.prompt(
        `Reason for permanently closing ${name}? Restaurant employees will not be able to login.`,
        permanentCloseReasonOf(row) || "Closed by admin"
      );
      if (reason === null) return;

      const confirmed = window.confirm(
        `Permanent close ${name}? This will block restaurant employee login.`
      );
      if (!confirmed) return;

      try {
        await restaurantAdminService.setRestaurantPermanentClose(restaurantId, {
          isPermanentlyClosed: true,
          reason: String(reason || "Closed by admin").trim() || "Closed by admin",
        });
        setPopupMessage(`${name} permanently closed. Restaurant employees cannot login now.`);
        await load();
      } catch (err) {
        setError(err);
      }
      return;
    }

    try {
      await restaurantAdminService.setRestaurantPermanentClose(restaurantId, {
        isPermanentlyClosed: false,
        status: "APPROVED",
        isActive: true,
        isOpen: false,
      });
      setPopupMessage(`${name} reopened. Restaurant employees can login again.`);
      await load();
    } catch (err) {
      setError(err);
    }
  }

  async function approveRegistration(req) {
    const id = req?.id || req?.requestId;
    if (!id) return;

    const cut = Number(
      requestCuts[id] ??
      req.adminCommissionPercent ??
      req.admin_commission_percent ??
      0
    );

    if (!Number.isFinite(cut) || cut < 0 || cut > 100) {
      setError("Admin cut must be between 0 and 100");
      return;
    }

    setReviewingId(id);
    setError("");

    try {
      const res = await restaurantAdminService.approveRestaurantRegistrationRequest(
        id,
        { adminCommissionPercent: cut }
      );

      const tempPassword = res?.tempPassword || res?.data?.tempPassword;

      setPopupMessage(
        tempPassword
          ? `Restaurant approved. Owner temporary password: ${tempPassword}`
          : "Restaurant approved. Owner credentials email queued."
      );

      await load();
    } catch (err) {
      setError(err);
    } finally {
      setReviewingId("");
    }
  }

  async function rejectRegistration(req) {
    const id = req?.id || req?.requestId;
    if (!id) return;

    const reason = window.prompt(
      "Reason for rejection?",
      "Documents/details not verified"
    );

    if (reason === null) return;

    setReviewingId(id);
    setError("");

    try {
      await restaurantAdminService.rejectRestaurantRegistrationRequest(id, {
        reason,
      });

      await load();
    } catch (err) {
      setError(err);
    } finally {
      setReviewingId("");
    }
  }

  async function openRegistrationReview(req) {
    const id = req?.id || req?.requestId;
    if (!id) return;

    setReviewLoading(true);
    setError("");

    try {
      const res = await restaurantAdminService.getRestaurantRegistrationRequest(id);
      const detail = unwrapRegistrationRequest(res) || req;
      const nextForm = registrationFormFromRequest(detail);

      setReviewRequest(detail);
      setReviewForm(nextForm);

      setRequestCuts((prev) => ({
        ...prev,
        [id]: nextForm.adminCommissionPercent ?? 0,
      }));
    } catch (err) {
      setError(err);

      const fallbackForm = registrationFormFromRequest(req);

      setReviewRequest(req);
      setReviewForm(fallbackForm);
    } finally {
      setReviewLoading(false);
    }
  }

  function closeRegistrationReview() {
    setReviewRequest(null);
    setReviewForm(null);
  }

  function setReviewField(key, value) {
    setReviewForm((prev) => ({ ...(prev || {}), [key]: value }));
  }

  function setReviewAddress(key, value) {
    setReviewForm((prev) => ({
      ...(prev || {}),
      address: {
        ...(prev?.address || {}),
        [key]: value,
      },
    }));
  }

  function setReviewBank(key, value) {
    setReviewForm((prev) => ({
      ...(prev || {}),
      bank: {
        ...(prev?.bank || {}),
        [key]: value,
      },
    }));
  }

  function setReviewDocumentUrl(key, value) {
    setReviewForm((prev) => ({
      ...(prev || {}),
      documents: {
        ...(prev?.documents || {}),
        [key]: {
          ...(prev?.documents?.[key] || {}),
          url: value,
        },
      },
    }));
  }

  async function saveRegistrationReview() {
    if (!reviewForm?.id) return;

    const cut = Number(reviewForm.adminCommissionPercent || 0);

    if (!Number.isFinite(cut) || cut < 0 || cut > 100) {
      setError("Admin cut must be between 0 and 100");
      return;
    }

    setReviewSaving(true);
    setError("");

    try {
      const res =
        await restaurantAdminService.updateRestaurantRegistrationRequest(
          reviewForm.id,
          makeRegistrationUpdatePayload(reviewForm)
        );

      const updated =
        unwrapRegistrationRequest(res)?.request ||
        unwrapRegistrationRequest(res) ||
        reviewRequest;

      setReviewRequest(updated);
      setReviewForm(registrationFormFromRequest(updated));

      setRequestCuts((prev) => ({
        ...prev,
        [reviewForm.id]: cut,
      }));

      await load();

      setPopupMessage("Registration details updated.");
    } catch (err) {
      setError(err);
    } finally {
      setReviewSaving(false);
    }
  }

  async function approveReviewedRegistration() {
    if (!reviewForm?.id) return;

    const cut = Number(reviewForm.adminCommissionPercent || 0);

    if (!Number.isFinite(cut) || cut < 0 || cut > 100) {
      setError("Admin cut must be between 0 and 100");
      return;
    }

    setReviewSaving(true);
    setError("");

    try {
      const res =
        await restaurantAdminService.updateRestaurantRegistrationRequest(
          reviewForm.id,
          makeRegistrationUpdatePayload(reviewForm)
        );

      const updated =
        unwrapRegistrationRequest(res)?.request ||
        unwrapRegistrationRequest(res) ||
        reviewRequest;

      setRequestCuts((prev) => ({
        ...prev,
        [reviewForm.id]: cut,
      }));

      setReviewSaving(false);

      await approveRegistration({
        ...updated,
        id: reviewForm.id,
        adminCommissionPercent: cut,
      });

      closeRegistrationReview();
    } catch (err) {
      setError(err);
      setReviewSaving(false);
    }
  }

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const paginatedRows = rows.slice((safePage - 1) * pageSize, safePage * pageSize);
  const startRow = rows.length ? (safePage - 1) * pageSize + 1 : 0;
  const endRow = Math.min(safePage * pageSize, rows.length);

  const requestTotalPages = Math.max(
    1,
    Math.ceil(registrationRequests.length / requestPageSize)
  );
  const safeRequestPage = Math.min(requestPage, requestTotalPages);
  const paginatedRegistrationRequests = registrationRequests.slice(
    (safeRequestPage - 1) * requestPageSize,
    safeRequestPage * requestPageSize
  );

  if (loading) return <LoadingBox label="Loading restaurants..." />;

  return (
    <div>
      <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>


          <h1 className="text-[26px] font-bold tracking-wide text-[#d96a7a] sm:text-[30px]">
            Restaurants
          </h1>

          {/* <p className="mt-1 max-w-[780px] text-[13px] font-medium leading-5 text-slate-500">
            Same super-admin style page to create restaurants, manage open/active status, rating, address, delivery timing and service radius.
          </p> */}
        </div>

        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:justify-end">
          <Button variant="ghost" onClick={load}>
            <RefreshCw size={16} />
            Refresh
          </Button>

          <Button onClick={startCreate}>
            <Plus size={16} />
            Add Restaurant
          </Button>
        </div>
      </div>

      <ErrorBox error={error} onRetry={load} />

      {popupMessage ? (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-slate-950/50 px-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-[20px] border border-[#f2c6cc] bg-white p-5 shadow-2xl">
            <div className="mb-3 flex items-center gap-2 text-[18px] font-bold text-[#d86a78]">
              <CheckCircle2 size={20} />
              Success
            </div>
            <p className="break-words text-[14px] font-medium leading-6 text-slate-600">
              {popupMessage}
            </p>
            <div className="mt-5 flex justify-end">
              <Button onClick={() => setPopupMessage("")}>OK</Button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <SummaryMetricCard label="Total Restaurants" value={stats.total} tone="pink" />
        <SummaryMetricCard label="Active Restaurants" value={stats.active} tone="green" />
        <SummaryMetricCard label="Open Now" value={stats.open} tone="pink" />
        <SummaryMetricCard label="Permanent Closed" value={stats.permanentClosed} tone="red" />
        <SummaryMetricCard label="Pending Signup" value={stats.pendingRequests} tone="yellow" />
      </div>

      <div className="relative z-30 mb-5 grid grid-cols-1 gap-3 rounded-[20px] border border-[#f2c6cc] bg-white p-4 sm:p-5 md:grid-cols-[1fr_220px] md:items-end">
        <Input
          label="Search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Restaurant name, city, phone"
        />

        <StatusFilter value={status} onChange={setStatus} />
      </div>

      {view === "list" && registrationRequests.length > 0 ? (
        <Card className="mb-5 border-amber-200 bg-amber-50/20">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="flex items-center gap-2 text-[20px] font-bold text-[#d96a7a]">
                {/* <FileText size={18} className="text-amber-500" /> */}
                Pending restaurant registrations
              </h2>
              <p className="text-[12px] text-slate-500">
                Review owner documents, set admin cut %, then approve or reject.
              </p>
            </div>

            <StatusPill value={`${registrationRequests.length} Pending`} />
          </div>

          <TableShell
            columns={["Restaurant", "Owner", "Email", "Phone", "Admin Cut %", "Actions"]}
            // minWidth={1040}
          >
            {paginatedRegistrationRequests.map((req) => {
              const id = req.id || req.requestId;
              const payload =
                req.payload || req.requestPayload || req.request_payload || {};

              return (
                <tr key={id} className="border-b last:border-0">
                  <td className="px-4 py-4">
                    <div className="font-semibold text-slate-900">
                      {req.restaurantName ||
                        req.restaurant_name ||
                        payload.restaurantName ||
                        "-"}
                    </div>
                  </td>

                  <td className="px-4 py-4">
                    <div className="text-[13px] font-medium text-slate-600">
                      {req.ownerName || req.owner_name || payload.ownerName || "-"}
                    </div>
                  </td>

                  <td className="px-4 py-4">
                    <div className="max-w-[240px] truncate text-[13px] font-medium text-slate-600" title={req.ownerEmail || req.owner_email || payload.ownerEmail || ""}>
                      {req.ownerEmail ||
                        req.owner_email ||
                        payload.ownerEmail ||
                        "-"}
                    </div>
                  </td>

                  <td className="px-4 py-4">
                    <div className="whitespace-nowrap text-[13px] font-medium text-slate-600">
                      {req.ownerPhone ||
                        req.owner_phone ||
                        payload.ownerPhone ||
                        "-"}
                    </div>
                  </td>

                  <td className="px-4 py-4">
                    <div className="flex max-w-[160px] items-center gap-2">
                      <Percent size={14} className="text-[#d86a78]" />
                      <Input
                        label=""
                        type="number"
                        min="0"
                        max="100"
                        value={requestCuts[id] ?? 0}
                        onChange={(e) =>
                          setRequestCuts((prev) => ({
                            ...prev,
                            [id]: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="mt-1 text-[11px] text-slate-400">
                      ₹100 item → ₹
                      {(100 + Number(requestCuts[id] || 0)).toFixed(2)} before
                      delivery
                    </div>
                  </td>

                  <td className="px-4 py-4">
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="soft"
                        size="sm"
                        onClick={() => openRegistrationReview(req)}
                        disabled={reviewingId === id || reviewLoading}
                      >
                        <Eye size={14} />
                        View
                      </Button>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => rejectRegistration(req)}
                        disabled={reviewingId === id}
                      >
                        <XCircle size={14} />
                        Reject
                      </Button>

                      <Button
                        size="sm"
                        onClick={() => approveRegistration(req)}
                        disabled={reviewingId === id}
                      >
                        <CheckCircle2 size={14} />
                        Approve
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </TableShell>

          <PaginationBar
            page={safeRequestPage}
            totalPages={requestTotalPages}
            totalItems={registrationRequests.length}
            pageSize={requestPageSize}
            onPageChange={setRequestPage}
            onPageSizeChange={setRequestPageSize}
            itemLabel="requests"
          />
        </Card>
      ) : null}

      {reviewForm ? (
        <div className="fixed inset-0 z-[1000] flex items-start justify-center overflow-y-auto bg-slate-950/60 px-3 py-6 backdrop-blur-sm">
          <div className="w-full max-w-6xl rounded-[22px] border border-[#f2c6cc] bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between gap-3 rounded-t-[22px] border-b border-[#f2c6cc] bg-white px-5 py-4">
              <div>
                <h2 className="text-[22px] font-bold text-slate-900">
                  Review restaurant registration
                </h2>
                <p className="text-[12px] text-slate-500">
                  Check and edit submitted restaurant, owner, bank, address,
                  documents and admin cut before approval.
                </p>
              </div>

              <div className="flex flex-wrap items-center justify-end gap-2">
                <Button variant="ghost" onClick={closeRegistrationReview}>
                  Close
                </Button>

                <Button onClick={saveRegistrationReview} disabled={reviewSaving}>
                  <Save size={15} />
                  {reviewSaving ? "Saving..." : "Save Changes"}
                </Button>

                <Button
                  onClick={approveReviewedRegistration}
                  disabled={reviewSaving || reviewingId === reviewForm.id}
                >
                  <CheckCircle2 size={15} />
                  Approve
                </Button>
              </div>
            </div>

            <div className="space-y-5 p-5">
              <Card className="border-slate-200 shadow-none">
                <h3 className="mb-3 text-[16px] font-bold text-[#d86a78]">
                  Restaurant details
                </h3>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  <Input
                    label="Restaurant Name"
                    value={reviewForm.restaurantName || ""}
                    onChange={(e) =>
                      setReviewField("restaurantName", e.target.value)
                    }
                  />
                  <Input
                    label="Restaurant Code"
                    value={reviewForm.restaurantCode || ""}
                    onChange={(e) =>
                      setReviewField("restaurantCode", e.target.value)
                    }
                  />
                  <Input
                    label="FSSAI Number"
                    value={reviewForm.fssaiNumber || ""}
                    onChange={(e) =>
                      setReviewField("fssaiNumber", e.target.value)
                    }
                  />
                  <Input
                    label="GST Number"
                    value={reviewForm.gstNumber || ""}
                    onChange={(e) => setReviewField("gstNumber", e.target.value)}
                  />
                  <Input
                    label="PAN Number"
                    value={reviewForm.panNumber || ""}
                    onChange={(e) => setReviewField("panNumber", e.target.value)}
                  />
                  <Input
                    label="Preparation Time Mins"
                    type="number"
                    value={reviewForm.preparationTimeMins || ""}
                    onChange={(e) =>
                      setReviewField("preparationTimeMins", e.target.value)
                    }
                  />
                  <Input
                    label="Service Radius Km"
                    type="number"
                    value={reviewForm.serviceRadiusKm || ""}
                    onChange={(e) =>
                      setReviewField("serviceRadiusKm", e.target.value)
                    }
                  />
                  <Input
                    label="Min Order Value"
                    type="number"
                    value={reviewForm.minOrderValue || ""}
                    onChange={(e) =>
                      setReviewField("minOrderValue", e.target.value)
                    }
                  />
                  <Input
                    label="Packaging Charge"
                    type="number"
                    value={reviewForm.packagingCharge || ""}
                    onChange={(e) =>
                      setReviewField("packagingCharge", e.target.value)
                    }
                  />
                  <Input
                    label="Admin Cut %"
                    type="number"
                    min="0"
                    max="100"
                    value={reviewForm.adminCommissionPercent || ""}
                    onChange={(e) => {
                      setReviewField("adminCommissionPercent", e.target.value);
                      setRequestCuts((prev) => ({
                        ...prev,
                        [reviewForm.id]: e.target.value,
                      }));
                    }}
                  />
                  <Input
                    label="Delivery Time Min"
                    type="number"
                    value={reviewForm.deliveryTimeMin || ""}
                    onChange={(e) =>
                      setReviewField("deliveryTimeMin", e.target.value)
                    }
                  />
                  <Input
                    label="Delivery Time Max"
                    type="number"
                    value={reviewForm.deliveryTimeMax || ""}
                    onChange={(e) =>
                      setReviewField("deliveryTimeMax", e.target.value)
                    }
                  />
                </div>

                <div className="mt-3">
                  <Textarea
                    label="Restaurant Description"
                    value={reviewForm.description || ""}
                    onChange={(e) =>
                      setReviewField("description", e.target.value)
                    }
                  />
                </div>
              </Card>

              <Card className="border-slate-200 shadow-none">
                <h3 className="mb-3 text-[16px] font-bold text-[#d86a78]">
                  Owner details
                </h3>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  <Input
                    label="Owner Name"
                    value={reviewForm.ownerName || ""}
                    onChange={(e) => setReviewField("ownerName", e.target.value)}
                  />
                  <Input
                    label="Owner Email"
                    type="email"
                    value={reviewForm.ownerEmail || ""}
                    onChange={(e) =>
                      setReviewField("ownerEmail", e.target.value)
                    }
                  />
                  <Input
                    label="Owner Phone"
                    value={reviewForm.ownerPhone || ""}
                    onChange={(e) =>
                      setReviewField("ownerPhone", e.target.value)
                    }
                  />
                  <Input
                    label="Father Name"
                    value={reviewForm.fatherName || ""}
                    onChange={(e) =>
                      setReviewField("fatherName", e.target.value)
                    }
                  />
                  <Input
                    label="Date of Birth"
                    type="date"
                    value={reviewForm.dateOfBirth || ""}
                    onChange={(e) =>
                      setReviewField("dateOfBirth", e.target.value)
                    }
                  />
                  <Select
                    label="Gender"
                    value={reviewForm.gender || ""}
                    onChange={(e) => setReviewField("gender", e.target.value)}
                  >
                    <option value="">Select gender</option>
                    <option value="MALE">MALE</option>
                    <option value="FEMALE">FEMALE</option>
                    <option value="OTHER">OTHER</option>
                  </Select>
                  <Input
                    label="Aadhaar Number"
                    value={reviewForm.aadhaarNumber || ""}
                    onChange={(e) =>
                      setReviewField("aadhaarNumber", e.target.value)
                    }
                  />
                </div>
              </Card>

              <Card className="border-slate-200 shadow-none">
                <h3 className="mb-3 text-[16px] font-bold text-[#d86a78]">
                  Address & service location
                </h3>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                  <div className="md:col-span-2">
                    <Input
                      label="Address Line 1"
                      value={reviewForm.address?.addressLine1 || ""}
                      onChange={(e) =>
                        setReviewAddress("addressLine1", e.target.value)
                      }
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Input
                      label="Address Line 2"
                      value={reviewForm.address?.addressLine2 || ""}
                      onChange={(e) =>
                        setReviewAddress("addressLine2", e.target.value)
                      }
                    />
                  </div>

                  <Input
                    label="City"
                    value={reviewForm.address?.city || ""}
                    onChange={(e) => setReviewAddress("city", e.target.value)}
                  />
                  <Input
                    label="State"
                    value={reviewForm.address?.state || ""}
                    onChange={(e) => setReviewAddress("state", e.target.value)}
                  />
                  <Input
                    label="Pincode"
                    value={reviewForm.address?.pincode || ""}
                    onChange={(e) => setReviewAddress("pincode", e.target.value)}
                  />
                  <Input
                    label="Latitude"
                    value={reviewForm.address?.lat || ""}
                    onChange={(e) => setReviewAddress("lat", e.target.value)}
                  />
                  <Input
                    label="Longitude"
                    value={reviewForm.address?.lng || ""}
                    onChange={(e) => setReviewAddress("lng", e.target.value)}
                  />
                </div>
              </Card>

              <Card className="border-slate-200 shadow-none">
                <h3 className="mb-3 text-[16px] font-bold text-[#d86a78]">
                  Bank details
                </h3>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
                  <Input
                    label="Account Holder"
                    value={reviewForm.bank?.accountHolderName || ""}
                    onChange={(e) =>
                      setReviewBank("accountHolderName", e.target.value)
                    }
                  />
                  <Input
                    label="Bank Name"
                    value={reviewForm.bank?.bankName || ""}
                    onChange={(e) => setReviewBank("bankName", e.target.value)}
                  />
                  <Input
                    label="Account Number"
                    value={reviewForm.bank?.accountNumber || ""}
                    onChange={(e) =>
                      setReviewBank("accountNumber", e.target.value)
                    }
                  />
                  <Input
                    label="IFSC Code"
                    value={reviewForm.bank?.ifscCode || ""}
                    onChange={(e) => setReviewBank("ifscCode", e.target.value)}
                  />
                  <Select
                    label="Account Type"
                    value={reviewForm.bank?.accountType || ""}
                    onChange={(e) => setReviewBank("accountType", e.target.value)}
                  >
                    <option value="">Select type</option>
                    <option value="SAVINGS">SAVINGS</option>
                    <option value="CURRENT">CURRENT</option>
                  </Select>
                  <Input
                    label="UPI ID"
                    value={reviewForm.bank?.upiId || ""}
                    onChange={(e) => setReviewBank("upiId", e.target.value)}
                  />
                </div>
              </Card>

              <Card className="border-slate-200 shadow-none">
                <h3 className="mb-3 text-[16px] font-bold text-[#d86a78]">
                  Documents
                </h3>

                {Object.keys(reviewForm.documents || {}).length ? (
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {Object.entries(reviewForm.documents || {}).map(([key, doc]) => {
                      const url = resolveAuthUploadUrl(
                        doc?.url || doc?.path || doc
                      );

                      return (
                        <div
                          key={key}
                          className="rounded-[14px] border border-[#f2c6cc] bg-[#fffafb] p-3"
                        >
                          <div className="mb-2 flex items-center justify-between gap-2">
                            <div className="text-[12px] font-semibold uppercase tracking-wide text-slate-500">
                              {key}
                            </div>

                            {url ? (
                              <a
                                href={url}
                                target="_blank"
                                rel="noreferrer"
                                className="text-[12px] font-semibold text-[#d86a78] hover:underline"
                              >
                                Open
                              </a>
                            ) : null}
                          </div>

                          <Input
                            label="Document URL"
                            value={doc?.url || doc?.path || ""}
                            onChange={(e) =>
                              setReviewDocumentUrl(key, e.target.value)
                            }
                          />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <EmptyState
                    title="No documents found"
                    subtitle="Owner submitted no readable document links."
                    icon={<FileText size={22} />}
                  />
                )}
              </Card>
            </div>
          </div>
        </div>
      ) : null}

      {view === "list" ? (
        <Card>
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-[20px] font-bold text-[#d96a7a]">
                Restaurant List
              </h2>
              <p className="text-[12px] text-slate-500">
                Manage restaurants, status, visibility and employees.
              </p>
            </div>

            <div className="flex h-11 w-11 items-center justify-center rounded-[14px] bg-[#fff0f2] text-[#d86a78]">
              <Building2 size={20} />
            </div>
          </div>

          {rows.length ? (
            <>
              <div className="grid grid-cols-1 gap-3 xl:hidden">
                {paginatedRows.map((row, idx) => (
                  <div
                    key={restaurantIdOf(row) || idx}
                    className="rounded-[16px] border border-[#f2c6cc] bg-white p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 items-start gap-3">
                        {row.logoUrl || row.logo_url ? (
                          <img
                            src={resolveRestaurantFoodImageUrl(row.logoUrl || row.logo_url)}
                            alt={row.name || "Restaurant logo"}
                            className="h-11 w-11 shrink-0 rounded-full object-cover"
                          />
                        ) : (
                          <div className="h-11 w-11 shrink-0 rounded-full bg-[#fff0f2]" />
                        )}
                        <div className="min-w-0">
                          <button
                            type="button"
                            onClick={() =>
                              navigate(
                                `/admin/food-dashboard/restaurants/${restaurantIdOf(row)}`
                              )
                            }
                            className="break-words text-left text-[15px] font-bold text-slate-900 hover:text-[#d86a78] hover:underline"
                          >
                            {row.name || "-"}
                          </button>
                          <div className="mt-1 flex flex-wrap gap-2">
                            <StatusPill value={row.status || "-"} />
                            {isRestaurantPermanentlyClosed(row) ? (
                              <StatusPill value="Permanent Closed" />
                            ) : null}
                            {Boolean(row.isRecommended ?? row.is_recommended) ? (
                              <span className="inline-flex rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-bold uppercase text-emerald-700">
                                Yes
                              </span>
                            ) : (
                              <StatusPill value="No" />
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex shrink-0 items-center justify-end gap-1">
                        <ActionIconButton
                          title="View restaurant"
                          tone="pink"
                          onClick={() =>
                            navigate(
                              `/admin/food-dashboard/restaurants/${restaurantIdOf(row)}`
                            )
                          }
                        >
                          <Eye size={15} />
                        </ActionIconButton>
                        <ActionIconButton
                          title="Edit restaurant"
                          tone="pink"
                          onClick={() => startEdit(row)}
                        >
                          <Edit3 size={15} />
                        </ActionIconButton>

                        <ActionIconButton
                          title={isRestaurantPermanentlyClosed(row) ? "Reopen restaurant" : "Permanent close restaurant"}
                          tone={isRestaurantPermanentlyClosed(row) ? "green" : "red"}
                          onClick={() => togglePermanentClose(row)}
                        >
                          {isRestaurantPermanentlyClosed(row) ? <RefreshCw size={15} /> : <XCircle size={15} />}
                        </ActionIconButton>
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div className="rounded-[12px] bg-[#fffafb] p-3">
                        <div className="text-[11px] font-bold uppercase text-slate-400">Phone</div>
                        <div className="mt-1 break-words text-[13px] font-semibold text-slate-700">{row.phone || "-"}</div>
                      </div>
                      <div className="rounded-[12px] bg-[#fffafb] p-3">
                        <div className="text-[11px] font-bold uppercase text-slate-400">Email</div>
                        <div className="mt-1 break-all text-[13px] font-semibold text-slate-700">{row.email || "-"}</div>
                      </div>
                      <div className="rounded-[12px] bg-[#fffafb] p-3">
                        <div className="text-[11px] font-bold uppercase text-slate-400">Location</div>
                        <div className="mt-1 flex items-center gap-2 break-words text-[13px] font-semibold text-slate-700">
                          <MapPin size={14} className="shrink-0 text-[#d86a78]" />
                          {row.city || row.address?.city || "-"}
                        </div>
                      </div>
                      <div className="rounded-[12px] bg-[#fffafb] p-3">
                        <div className="text-[11px] font-bold uppercase text-slate-400">Admin Cut</div>
                        <div className="mt-1">
                          <StatusPill
                            value={`${Number(
                              row.adminCommissionPercent ??
                              row.admin_commission_percent ??
                              0
                            )}%`}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-3">
                      <div className="flex items-center gap-2">
                        <span className="text-[12px] font-bold text-slate-500">Open</span>
                        <Switch
                          checked={boolOf(row.isOpen ?? row.is_open, false)}
                          onChange={() => toggle(row, "open")}
                          disabled={isRestaurantPermanentlyClosed(row)}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[12px] font-bold text-slate-500">Active</span>
                        <Switch
                          checked={boolOf(row.isActive ?? row.is_active, false)}
                          onChange={() => toggle(row, "active")}
                          disabled={isRestaurantPermanentlyClosed(row)}
                        />
                      </div>

                      <Button
                        variant={isRestaurantPermanentlyClosed(row) ? "soft" : "danger"}
                        size="sm"
                        onClick={() => togglePermanentClose(row)}
                      >
                        {isRestaurantPermanentlyClosed(row) ? "Reopen" : "Permanent Close"}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="hidden xl:block [&_thead_tr]:border-b [&_thead_tr]:text-[12px] [&_thead_tr]:font-bold [&_thead_tr]:uppercase [&_thead_tr]:tracking-wide [&_thead_tr]:text-[#d96a7a] [&_thead_th]:px-4 [&_thead_th]:py-3">
                <TableShell
                  columns={[
                    "Status",
                    "Restaurant",
                    "Phone",
                    "Email",
                    "Location",
                    "Admin Cut",
                    "Recommended",
                    "Permanent",
                    "Open",
                    "Active",
                    "Actions",
                  ]}
                  minWidth={1220}
                >
                  {paginatedRows.map((row, idx) => (
                    <tr
                      key={restaurantIdOf(row) || idx}
                      className="border-b last:border-0"
                    >
                      <td className="w-[150px] px-4 py-4">
                        <div className="flex items-center gap-2 whitespace-nowrap">
                          {row.logoUrl || row.logo_url ? (
                            <img
                              src={resolveRestaurantFoodImageUrl(row.logoUrl || row.logo_url)}
                              alt={row.name || "Restaurant logo"}
                              className="h-8 w-8 rounded-full object-cover"
                              onClick={() =>
                                navigate(
                                  `/admin/food-dashboard/restaurants/${restaurantIdOf(
                                    row
                                  )}`
                                )
                              }
                            />
                          ) : null}
                          <StatusPill value={row.status || "-"} />
                          {isRestaurantPermanentlyClosed(row) ? (
                            <StatusPill value="Permanent Closed" />
                          ) : null}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <button
                          type="button"
                          onClick={() =>
                            navigate(
                              `/admin/food-dashboard/restaurants/${restaurantIdOf(row)}`
                            )
                          }
                          className="text-left font-semibold text-slate-900 hover:text-[#d86a78] hover:underline"
                        >
                          {row.name || "-"}
                        </button>

                        {/* <div className="mt-1 break-all text-[11px] text-slate-400">
                        Code: {row.code || "-"}
                      </div> */}
                      </td>

                      <td className="px-4 py-4">
                        <div className="whitespace-nowrap text-[13px] font-medium text-slate-600">
                          {row.phone || "-"}
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        <div className="max-w-[220px] truncate text-[13px] font-medium text-slate-600" title={row.email || ""}>
                          {row.email || "-"}
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        <div className="flex items-start gap-2 text-slate-700">
                          <MapPin size={15} className="mt-0.5 text-[#d86a78]" />
                          <span>{row.city || row.address?.city || "-"}</span>
                        </div>

                        {/* <div className="mt-1 text-[12px] text-slate-500">
                        {row.state || row.address?.state || ""}{" "}
                        {row.pincode || row.address?.pincode || ""}
                      </div> */}
                      </td>

                      <td className="px-4 py-4">
                        <StatusPill
                          value={`${Number(
                            row.adminCommissionPercent ??
                            row.admin_commission_percent ??
                            0
                          )}%`}
                        />
                      </td>

<td className="px-4 py-4">
                        {Boolean(row.isRecommended ?? row.is_recommended) ? (
                          <span className="inline-flex rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-bold uppercase text-emerald-700">
                            Yes
                          </span>
                        ) : (
                          <StatusPill value="No" />
                        )}
                      </td>

                      <td className="px-4 py-4">
                        {isRestaurantPermanentlyClosed(row) ? (
                          <StatusPill value="Closed" />
                        ) : (
                          <StatusPill value="No" />
                        )}
                      </td>

                      <td className="px-4 py-4">
                        <Switch
                          checked={boolOf(row.isOpen ?? row.is_open, false)}
                          onChange={() => toggle(row, "open")}
                          disabled={isRestaurantPermanentlyClosed(row)}
                        />
                      </td>

                      <td className="px-4 py-4">
                        <Switch
                          checked={boolOf(row.isActive ?? row.is_active, false)}
                          onChange={() => toggle(row, "active")}
                          disabled={isRestaurantPermanentlyClosed(row)}
                        />
                      </td>

                      

                      <td className="w-[120px] px-4 py-4 text-center align-middle">
                        <div className="mx-auto flex w-fit items-center justify-center gap-1">
                          <ActionIconButton
                            title="View restaurant"
                            tone="pink"
                            onClick={() =>
                              navigate(
                                `/admin/food-dashboard/restaurants/${restaurantIdOf(
                                  row
                                )}`
                              )
                            }
                          >
                            <Eye size={15} />
                          </ActionIconButton>

                          <ActionIconButton
                            title="Edit restaurant"
                            tone="pink"
                            onClick={() => startEdit(row)}
                          >
                            <Edit3 size={15} />
                          </ActionIconButton>

                          <ActionIconButton
                            title={isRestaurantPermanentlyClosed(row) ? "Reopen restaurant" : "Permanent close restaurant"}
                            tone={isRestaurantPermanentlyClosed(row) ? "green" : "red"}
                            onClick={() => togglePermanentClose(row)}
                          >
                            {isRestaurantPermanentlyClosed(row) ? <RefreshCw size={15} /> : <XCircle size={15} />}
                          </ActionIconButton>
                        </div>
                      </td>
                    </tr>
                  ))}
                </TableShell>
              </div>

              <div className="mt-4 text-[12px] font-medium text-slate-500">
                Showing {startRow}-{endRow} of {rows.length} restaurants
              </div>

              <PaginationBar
                page={safePage}
                totalPages={totalPages}
                totalItems={rows.length}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                itemLabel="restaurants"
              />
            </>
          ) : (
            <EmptyState
              title="No restaurants found"
              subtitle="Create your first restaurant from the button above."
              icon={<Store size={22} />}
            />
          )}
        </Card>
      ) : (
        <Card>
          <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-[22px] font-bold text-[#d86a78]">
                {selected ? "Edit Restaurant" : "Create Restaurant"}
              </h2>
              <p className="text-[12px] text-slate-500">
                Keep same structure as warehouse/admin forms.
              </p>
            </div>

            <Button variant="ghost" onClick={() => setView("list")}>
              Back to list
            </Button>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            <Input
              label="Code"
              value={form.code || ""}
              onChange={(e) => setField("code", e.target.value)}
              placeholder="ROM-PIZZA-001"
            />

            <Input
              label="Name"
              value={form.name || ""}
              onChange={(e) => setField("name", e.target.value)}
              placeholder="Rominus Pizza"
            />

            <Input
              label="Phone"
              value={form.phone || ""}
              onChange={(e) => setField("phone", e.target.value)}
            />

            <Input
              label="Email"
              value={form.email || ""}
              onChange={(e) => setField("email", e.target.value)}
            />

            <Input
              label="Father Name"
              value={form.fatherName || ""}
              onChange={(e) => setField("fatherName", e.target.value)}
            />

            <Input
              label="Date of Birth"
              type="date"
              value={form.dateOfBirth || ""}
              onChange={(e) => setField("dateOfBirth", e.target.value)}
            />

            <Select
              label="Gender"
              value={form.gender || ""}
              onChange={(e) => setField("gender", e.target.value)}
            >
              <option value="">Select gender</option>
              <option value="MALE">MALE</option>
              <option value="FEMALE">FEMALE</option>
              <option value="OTHER">OTHER</option>
            </Select>

            <Input
              label="Aadhaar Number"
              value={form.aadhaarNumber || ""}
              onChange={(e) => setField("aadhaarNumber", e.target.value)}
            />

            <Select
              label="Account Type"
              value={form.accountType || "SAVINGS"}
              onChange={(e) => setField("accountType", e.target.value)}
            >
              <option value="SAVINGS">SAVINGS</option>
              <option value="CURRENT">CURRENT</option>
            </Select>

            <ImageUploadField
              label="Aadhaar Image"
              value={form.aadhaarImageUrl || ""}
              onUploaded={(url) => setField("aadhaarImageUrl", url)}
            />

            <ImageUploadField
              label="Logo Image"
              value={form.logoUrl || ""}
              onUploaded={(url) => setField("logoUrl", url)}
            />

            <ImageUploadField
              label="Cover Image"
              value={form.coverImageUrl || ""}
              onUploaded={(url) => setField("coverImageUrl", url)}
            />

            <Input
              label="Address Line 1"
              value={form.address?.addressLine1 || ""}
              onChange={(e) => setAddress("addressLine1", e.target.value)}
            />

            <Input
              label="Address Line 2"
              value={form.address?.addressLine2 || ""}
              onChange={(e) => setAddress("addressLine2", e.target.value)}
            />

            <Input
              label="City"
              value={form.address?.city || ""}
              onChange={(e) => setAddress("city", e.target.value)}
            />

            <Input
              label="State"
              value={form.address?.state || ""}
              onChange={(e) => setAddress("state", e.target.value)}
            />

            <Input
              label="Pincode"
              value={form.address?.pincode || ""}
              onChange={(e) => setAddress("pincode", e.target.value)}
            />

            <Input
              label="Latitude"
              type="number"
              value={form.address?.lat || ""}
              onChange={(e) => setAddress("lat", e.target.value)}
            />

            <Input
              label="Longitude"
              type="number"
              value={form.address?.lng || ""}
              onChange={(e) => setAddress("lng", e.target.value)}
            />

            <Input
              label="Service Radius Km"
              type="number"
              value={form.serviceRadiusKm || ""}
              onChange={(e) => setField("serviceRadiusKm", e.target.value)}
            />

            <Input
              label="Min Order Value"
              type="number"
              value={form.minOrderValue || ""}
              onChange={(e) => setField("minOrderValue", e.target.value)}
            />

            <Input
              label="Packaging Charge"
              type="number"
              value={form.packagingCharge || ""}
              onChange={(e) => setField("packagingCharge", e.target.value)}
            />

            <Input
              label="Admin Cut %"
              type="number"
              min="0"
              max="100"
              value={form.adminCommissionPercent || ""}
              onChange={(e) =>
                setField("adminCommissionPercent", e.target.value)
              }
            />

            <Input
              label="Preparation Time Mins"
              type="number"
              value={form.preparationTimeMins || ""}
              onChange={(e) =>
                setField("preparationTimeMins", e.target.value)
              }
            />

            <Input
              label="Delivery Time Min"
              type="number"
              value={form.deliveryTimeMin || ""}
              onChange={(e) => setField("deliveryTimeMin", e.target.value)}
            />

            <Input
              label="Delivery Time Max"
              type="number"
              value={form.deliveryTimeMax || ""}
              onChange={(e) => setField("deliveryTimeMax", e.target.value)}
            />

            <Input
              label="Rating Avg"
              type="number"
              value={form.ratingAvg || ""}
              onChange={(e) => setField("ratingAvg", e.target.value)}
            />

            <Input
              label="Rating Count"
              type="number"
              value={form.ratingCount || ""}
              onChange={(e) => setField("ratingCount", e.target.value)}
            />

            <Select
              label="Status"
              value={form.status || "APPROVED"}
              onChange={(e) => setField("status", e.target.value)}
            >
              <option value="APPROVED">APPROVED</option>
              <option value="PENDING">PENDING</option>
              <option value="SUSPENDED">SUSPENDED</option>
              <option value="BLOCKED">BLOCKED</option>
            </Select>
          </div>

          <div className="mt-3">
            <Textarea
              label="Description"
              value={form.description || ""}
              onChange={(e) => setField("description", e.target.value)}
            />
          </div>

          {isRestaurantPermanentlyClosed(form) ? (
            <div className="mt-4 rounded-[14px] border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] font-semibold text-rose-700">
              This restaurant is permanently closed. Use the Reopen action from the restaurant list/detail page before changing Open or Active status.
            </div>
          ) : null}

          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {["isActive", "isOpen", "isFeatured", "isRecommended"].map((key) => (
              <div
                key={key}
                className="flex items-center justify-between rounded-[14px] border border-[#f2c6cc] bg-[#fffafb] px-4 py-3"
              >
                <span className="text-[13px] font-semibold capitalize text-slate-600">
                  {key.replace("is", "")}
                </span>

                <Switch
                  checked={Boolean(form[key])}
                  onChange={(v) => setField(key, v)}
                  disabled={isRestaurantPermanentlyClosed(form) && ["isActive", "isOpen"].includes(key)}
                />
              </div>
            ))}
          </div>

          <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="ghost" onClick={() => setView("list")}>
              Cancel
            </Button>

            <Button onClick={submit} disabled={saving}>
              {saving
                ? "Saving..."
                : selected
                  ? "Update Restaurant"
                  : "Create Restaurant"}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}